import toast from 'react-hot-toast';
import { toastSuccessWithLink } from '~/utils/toastSuccessWithLink';
import { useTranslation } from 'react-i18next';
import {
  useAcceptedPriceNegotiations,
  usePendingPriceNegotiations,
  useRequestPriceNegotiation,
  useRequestTallyPriceNegotiation,
  useUpdateCart,
  vendorGroupCartKey,
} from '~/api';
import { Button } from '~/components/ui';
import { tabTriggerClass } from '~/components/ui/tabTriggerClass';
import { IncludeInventoryLengthsToggle } from '~/components/common/IncludeInventoryLengthsToggle';
import type {
  Cart,
  PlatformProduct,
  PlatformProductLength,
  UnitCode,
} from '~/graphql/graphql';
import {
  type ClientError,
  extractGraphQLErrorMessages,
  sellerLabel,
} from '~/utils';
import type { OrderInfo } from 'src/pages/order-builder/OrderBuider';
import { useId, useMemo, useState, type KeyboardEvent, type Ref } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import {
  setEditedTallyPrice,
  clearEditedTallyPrice,
  setEditedVendorGroupPrice,
  clearEditedVendorGroupPrice,
  editedPricesInUnit,
} from '~/redux/reducers/inventory';
import {
  getAddToCartQuantity,
  getPlatformProductDimensions,
  roundUpToBundleQty,
} from '~/utils/order';
import {
  SellerComparisonTable,
  type CannedTallyGroupData,
} from './SellerComparisonTable';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { FileText, ListPlus, MessageSquare, UserPlus } from 'lucide-react';
import { ToolbarActionButton } from './ToolbarActionButton';
import {
  RequestQuoteModal,
  type RequestQuoteLength,
} from './RequestQuoteModal';
import {
  defaultRequestQuantity,
  requestQuoteDisabledReason,
} from './requestQuote';
import { buildLengthFilterOptions } from './lengthFilterOptions';
import { LengthPicker, SelectedLengthChips } from './LengthFilterControls';
import type { LengthMode } from './lengthSelection';
import { NegotiatePriceModal } from './NegotiatePriceModal';
import type { SellerContactOrganization } from './OrgContactRow';
import {
  firstCurrency,
  groupTargetsByPrice,
  removeOrganization,
  partitionNegotiationResults,
  sendSerialPerSeller,
  storagePricesForTargets,
  targetsOfKind,
  negotiateDisabledReason,
  negotiationKey,
  uniqueSellerOrganizations,
  useNegotiationSelection,
  type NegotiationSelectionTarget,
} from './negotiationSelection';
import { AddSellerModal } from './AddSellerModal';
import { CreateRandomTallyModal } from './CreateRandomTallyModal';
import type {
  SellerInventoryForBuyer,
  SellerVendorInventoryGroupForBuyer,
} from '~/types/inventory';
import type { RouteSort } from '~/hooks/usePaginatedQuery';
import { ComparisonToolbar } from './ComparisonToolbar';
import { getComparisonFilterFields } from '~/pages/order-builder/lib/comparisonFilterFields';
import type { ComparisonSavedViews } from '~/pages/order-builder/lib/useComparisonSavedViews';
import type { Quantity, SellerComparisonFiltersInput } from '~/graphql/graphql';
import { minimumOrderInUnit } from '~/utils/targetOrderSize';
import { toStoragePriceOrNull } from '~/utils/unitConversion';

type PricesAndAvailabilityProps = {
  sellerInventories: SellerInventoryForBuyer[] | undefined | null;
  carts: Cart[];
  platformProductLengths: PlatformProductLength[] | undefined | null;
  selectedLengthIds: string[];
  effectiveLengthIds: string[];
  singleSelectedLengthId?: string;
  mode: LengthMode;
  onLengthsChange: (ids: string[]) => void;
  onModeChange: (mode: LengthMode) => void;
  orderInfo: OrderInfo;
  platformProduct: PlatformProduct;
  preferredUnit: UnitCode;
  onAddedToCart: (quantity: number) => void;
  transportVehicleId?: string | null;
  selectedLocationId?: string | null;
  selectedTallyName?: string;
  /** Set only in the order-builder's sourcing mode; routes the cart upsert to a sub-order of this parent order. */
  parentOrderId?: string;
  hasMoreData?: boolean | null;
  onSellerAdded?: () => void;
  vendorGroups?: SellerVendorInventoryGroupForBuyer[];
  isVendorGroupsLoading?: boolean;
  isLoadingMoreVendorGroups?: boolean;
  hasMoreVendorGroups?: boolean | null;
  vendorGroupsSentinelRef?: Ref<HTMLDivElement>;
  cannedTallyGroups?: CannedTallyGroupData[];
  isCannedTallyGroupsLoading?: boolean;
  isLoadingMoreCannedTallyGroups?: boolean;
  hasMoreCannedTallyGroups?: boolean | null;
  cannedTallyGroupsSentinelRef?: Ref<HTMLDivElement>;
  sort?: RouteSort;
  onSort?: (field: string, direction: 'asc' | 'desc' | null) => void;
  includeInventoryLengths: boolean;
  onIncludeInventoryLengthsChange: (checked: boolean) => void;
  comparisonViews: ComparisonSavedViews;
  comparisonFiltersInput: SellerComparisonFiltersInput;
};

export function PricesAndAvailability({
  sellerInventories,
  orderInfo,
  carts,
  platformProductLengths,
  selectedLengthIds,
  effectiveLengthIds,
  mode,
  platformProduct,
  preferredUnit,

  onLengthsChange,
  onModeChange,
  onAddedToCart,
  selectedLocationId,
  transportVehicleId,
  selectedTallyName,
  parentOrderId,
  onSellerAdded,
  vendorGroups,
  isVendorGroupsLoading,
  isLoadingMoreVendorGroups,
  hasMoreVendorGroups,
  vendorGroupsSentinelRef,
  cannedTallyGroups,
  isCannedTallyGroupsLoading,
  isLoadingMoreCannedTallyGroups,
  hasMoreCannedTallyGroups,
  cannedTallyGroupsSentinelRef,
  sort,
  onSort,
  includeInventoryLengths,
  onIncludeInventoryLengthsChange,
  comparisonViews,
  comparisonFiltersInput,
}: Readonly<PricesAndAvailabilityProps>) {
  const [showAddSellerModal, setShowAddSellerModal] = useState(false);
  const [showCreateRandomTallyModal, setShowCreateRandomTallyModal] =
    useState(false);
  const {
    selection: negotiationSelection,
    selectedTargets,
    setSelectedTargets,
  } = useNegotiationSelection();
  const negotiationTargets = useMemo(
    () => Array.from(selectedTargets.values()),
    [selectedTargets]
  );
  const [modalTargets, setModalTargets] = useState<
    NegotiationSelectionTarget[] | null
  >(null);
  const selectionScope = JSON.stringify([
    mode,
    effectiveLengthIds,
    comparisonFiltersInput,
    transportVehicleId,
    selectedTallyName,
  ]);
  const [prevSelectionScope, setPrevSelectionScope] = useState(selectionScope);
  if (selectionScope !== prevSelectionScope) {
    setPrevSelectionScope(selectionScope);
    setSelectedTargets(new Map());
  }
  const handleSellerRemoved = (organizationId: string) =>
    setSelectedTargets((previous) =>
      removeOrganization(previous, organizationId)
    );
  const [isSubmittingNegotiation, setIsSubmittingNegotiation] = useState(false);
  const [requestQuoteTargets, setRequestQuoteTargets] = useState<
    NegotiationSelectionTarget[] | null
  >(null);
  const { t } = useTranslation('order-builder');
  const navigate = useNavigate();
  const buyerCurrency = useSelector(
    (state: RootState) => state.auth.user?.organization.defaultCurrency
  );
  const { mutate: updateCart, mutateAsync: updateCartAsync } = useUpdateCart();

  const [, setSearchParams] = useSearchParams();

  const { mutateAsync: requestPriceNegotiationAsync } =
    useRequestPriceNegotiation();
  const { mutate: requestTallyPriceNegotiation } =
    useRequestTallyPriceNegotiation();
  const { data: pendingPriceNegotiationsData } = usePendingPriceNegotiations(
    platformProduct.id
  );
  const { data: acceptedPriceNegotiationsData } = useAcceptedPriceNegotiations(
    platformProduct.id
  );

  const pendingNegotiationKeys = useMemo(
    () =>
      new Set(
        (pendingPriceNegotiationsData?.pendingPriceNegotiations ?? []).flatMap(
          (ps) =>
            ps.platformProductLength
              ? [
                  negotiationKey(
                    ps.platformProductLength.id,
                    ps.sellingOrganization.id
                  ),
                ]
              : []
        )
      ),
    [pendingPriceNegotiationsData]
  );

  const pendingTallyNegotiationKeys = useMemo(
    () =>
      new Set(
        (pendingPriceNegotiationsData?.pendingPriceNegotiations ?? []).flatMap(
          (ps) =>
            ps.platformProductCannedTally
              ? [
                  negotiationKey(
                    ps.platformProductCannedTally.id,
                    ps.sellingOrganization.id
                  ),
                ]
              : []
        )
      ),
    [pendingPriceNegotiationsData]
  );

  const acceptedTallyPriceByKey = useMemo(
    () =>
      new Map(
        (
          acceptedPriceNegotiationsData?.acceptedPriceNegotiations ?? []
        ).flatMap((negotiation) =>
          negotiation.platformProductCannedTally && negotiation.offeredPrice
            ? [
                [
                  negotiationKey(
                    negotiation.platformProductCannedTally.id,
                    negotiation.sellingOrganization.id
                  ),
                  { ...negotiation.offeredPrice, unit: negotiation.unit },
                ] as const,
              ]
            : []
        )
      ),
    [acceptedPriceNegotiationsData]
  );

  const excludeOrganizationIds = useMemo(
    () =>
      [
        ...new Set(
          (sellerInventories ?? []).map((inv) => inv.facility.organization.id)
        ),
      ].sort(),
    [sellerInventories]
  );

  const dispatch = useDispatch();
  const editedTallyPriceRecord = useSelector(
    (state: RootState) => state.inventory.editedTallyPriceByKey
  );
  const editedTallyPriceByKey = useMemo(
    () => new Map(Object.entries(editedTallyPriceRecord)),
    [editedTallyPriceRecord]
  );
  const editedVendorGroupPriceRecord = useSelector(
    (state: RootState) => state.inventory.editedVendorGroupPriceByKey
  );
  const editedVendorGroupPriceByKey = useMemo(
    () => editedPricesInUnit(editedVendorGroupPriceRecord, preferredUnit),
    [editedVendorGroupPriceRecord, preferredUnit]
  );

  const lengthFilterOptions = useMemo(
    () =>
      buildLengthFilterOptions(
        platformProductLengths,
        {
          nominal: (length) =>
            t('order_builder.filters.length_nominal', { length }),
          actual: (length) =>
            t('order_builder.filters.length_actual', { length }),
        },
        { includeInventoryLengths }
      ),
    [platformProductLengths, includeInventoryLengths, t]
  );

  const cartIdentifiers = carts?.reduce<string[]>((identifiers, cart) => {
    const items = cart?.items ?? [];
    items.forEach((item) => {
      const lengthId = item?.platformProductsLengths?.id;
      const orgId = cart.sellingOrganization?.id;
      if (lengthId && orgId) {
        identifiers.push(vendorGroupCartKey(lengthId, orgId));
      }
    });
    return identifiers;
  }, []);

  const targetPrice = orderInfo?.targetPrice;

  const targetQuantity = orderInfo?.targetOrderSize ?? 0;

  const isCannedTally = mode === 'cannedTally';

  const comparisonFilterFields = useMemo(
    () => getComparisonFilterFields(t),
    [t]
  );
  const hasActiveComparisonFilter =
    Object.keys(comparisonFiltersInput).length > 0;
  const isEmptyForActiveComparisonFilter = isCannedTally
    ? !cannedTallyGroups?.length && !isCannedTallyGroupsLoading
    : !vendorGroups?.length && !isVendorGroupsLoading;

  const dimensionsOfLength = (platformProductLengthId: string) => {
    const length = platformProductLengths?.find(
      ({ id }) => id === platformProductLengthId
    );
    return length
      ? getPlatformProductDimensions(
          platformProduct,
          length.length,
          length.isNominal
        )
      : null;
  };

  const handleAddToCart = (
    platformProductLengthId: string,
    stock: number,
    minimumOrder: Quantity | null,
    facilityId: string,
    suggestedPrice: number | undefined,
    vendorGroupId: string
  ) => {
    if (!selectedLocationId) {
      toast.error(t('order_builder.shipping_address_required'));
      return;
    }

    if (!platformProductLengths) {
      return;
    }

    const productDimensions = dimensionsOfLength(platformProductLengthId);

    if (!productDimensions) {
      toast.error(t('order_builder.add_to_cart_error'));
      return;
    }

    const storageSuggestedPrice =
      suggestedPrice == null
        ? undefined
        : toStoragePriceOrNull(
            suggestedPrice,
            preferredUnit,
            platformProduct.standardUnitsCode,
            productDimensions
          );
    if (suggestedPrice != null && storageSuggestedPrice == null) {
      toast.error(
        t('order_builder.add_to_cart_price_unconvertible', {
          unit: platformProduct.standardUnitsCode,
        })
      );
      return;
    }

    const minimumQuantity = minimumOrderInUnit(
      minimumOrder,
      preferredUnit,
      productDimensions
    );
    if (minimumQuantity == null) {
      toast.error(t('order_builder.add_to_cart_error'));
      return;
    }

    const quantity = getAddToCartQuantity(
      Math.max(0, targetQuantity),
      stock,
      minimumQuantity
    );
    const bundleQuantity = roundUpToBundleQty(
      quantity,
      platformProduct.piecesPerBundle.value,
      preferredUnit,
      productDimensions.lengthInches,
      productDimensions.widthInches,
      productDimensions.thicknessInches
    );
    updateCart(
      {
        platformProductInputFilter: {
          id: platformProductLengthId,
          tally: false,
        },
        quantity: {
          value: bundleQuantity,
          unit: preferredUnit,
        },
        locationId: selectedLocationId,
        sellerOrganizationFacilityId: facilityId,
        suggestedPrice: storageSuggestedPrice ?? undefined,
        parentOrderId,
      },
      {
        onSuccess: () => {
          onAddedToCart(bundleQuantity);
          dispatch(clearEditedVendorGroupPrice({ key: vendorGroupId }));
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (!next.get('locationId')) {
              next.set('locationId', selectedLocationId || '');
            }
            return next;
          });
          toast.success(t('order_builder.add_to_cart_success'));
        },
        onError: (error) =>
          toast.error(
            t('order_builder.add_to_cart_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          ),
      }
    );
  };

  const handleVendorGroupPriceEdit = (
    vendorGroupId: string,
    newPrice: number
  ) => {
    dispatch(
      setEditedVendorGroupPrice({
        key: vendorGroupId,
        price: newPrice,
        unit: preferredUnit,
      })
    );
  };

  const handleTallyPriceEdit = (
    tallyId: string,
    orgId: string,
    newPrice: number
  ) => {
    dispatch(
      setEditedTallyPrice({
        key: negotiationKey(tallyId, orgId),
        price: newPrice,
      })
    );
  };

  const handleAddTallyToCart = async (
    tallyId: string,
    tallyCount: number,
    facilityId: string,
    suggestedPrice: number | undefined,
    sellingOrganizationId: string
  ) => {
    if (!selectedLocationId) {
      toast.error(t('order_builder.shipping_address_required'));
      return;
    }
    try {
      await updateCartAsync({
        locationId: selectedLocationId,
        sellerOrganizationFacilityId: facilityId,
        platformProductInputFilter: { id: tallyId, tally: true },
        quantity: { value: tallyCount, unit: 'TALLIES' },
        suggestedPrice,
        parentOrderId,
      });
      dispatch(
        clearEditedTallyPrice({
          key: negotiationKey(tallyId, sellingOrganizationId),
        })
      );
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!next.get('locationId')) {
          next.set('locationId', selectedLocationId || '');
        }
        return next;
      });
      toast.success(t('order_builder.add_to_cart_success'));
    } catch (error) {
      toast.error(
        t('order_builder.add_to_cart_error', {
          message: extractGraphQLErrorMessages(error as ClientError).join(', '),
        })
      );
    }
  };

  const pendingModeNegotiationKeys = isCannedTally
    ? pendingTallyNegotiationKeys
    : pendingNegotiationKeys;
  const requestTargets = useMemo(
    () =>
      (modalTargets ?? []).filter(
        (target) => !pendingModeNegotiationKeys.has(target.negotiationKey)
      ),
    [modalTargets, pendingModeNegotiationKeys]
  );
  const negotiationOrganizations: SellerContactOrganization[] = useMemo(
    () =>
      uniqueSellerOrganizations(modalTargets ?? []).map((organization) => ({
        ...organization,
        hasPendingNegotiation: !requestTargets.some(
          (requestTarget) =>
            requestTarget.sellingOrganizationId === organization.id
        ),
      })),
    [modalTargets, requestTargets]
  );
  const negotiationCurrency = firstCurrency(requestTargets);

  const handleSubmitNegotiation = (
    targetPrice: number | null,
    notes: string,
    recipientUserIds: string[],
    organizationIds: string[]
  ) => {
    const submittedTargets = requestTargets.filter((target) =>
      organizationIds.includes(target.sellingOrganizationId)
    );
    if (!modalTargets || submittedTargets.length === 0) return;
    const handledTargets = modalTargets;
    setIsSubmittingNegotiation(true);
    const currency = submittedTargets[0].currencyCode;
    const request = {
      targetPrice:
        targetPrice != null ? { amount: targetPrice, currency } : null,
      buyerNote: notes || null,
      recipientUserIds:
        recipientUserIds.length > 0 ? recipientUserIds : undefined,
    };
    const callbacks = {
      onSuccess: () => {
        toast.success(t('order_builder.price_negotiation_sent'));
        setModalTargets(null);
        setSelectedTargets((previous) => {
          const next = new Map(previous);
          handledTargets.forEach((target) =>
            next.delete(target.negotiationKey)
          );
          return next;
        });
        setIsSubmittingNegotiation(false);
      },
      onError: (error: ClientError) => {
        toast.error(extractGraphQLErrorMessages(error).join(', '));
        setIsSubmittingNegotiation(false);
      },
    };

    if (isCannedTally) {
      const tallyTargets = targetsOfKind(submittedTargets, 'tally');
      requestTallyPriceNegotiation(
        {
          ...request,
          sellingOrganizationIds: tallyTargets.map(
            (target) => target.sellingOrganizationId
          ),
          platformProductCannedTallyIds: tallyTargets.map(
            (target) => target.tallyId
          ),
          unit: platformProduct.standardUnitsCode,
        },
        callbacks
      );
      return;
    }

    const lengthTargets = targetsOfKind(submittedTargets, 'length');
    if (lengthTargets.length === 0) {
      setIsSubmittingNegotiation(false);
      return;
    }
    const storagePrices = storagePricesForTargets(
      lengthTargets,
      targetPrice,
      preferredUnit,
      platformProduct.standardUnitsCode,
      dimensionsOfLength
    );
    if (
      targetPrice != null &&
      lengthTargets.some((target) => storagePrices.get(target) == null)
    ) {
      toast.error(
        t('order_builder.price_negotiation_unconvertible', {
          unit: platformProduct.standardUnitsCode,
        })
      );
      setIsSubmittingNegotiation(false);
      return;
    }
    // One request per stored price: a price per preferred unit can differ per standard unit on each length.
    const priceGroups = Array.from(
      groupTargetsByPrice(
        lengthTargets,
        (target) => storagePrices.get(target) ?? null
      )
    );
    sendSerialPerSeller(
      priceGroups.map(([price, priceTargets]) => ({
        sellerIds: priceTargets.map((target) => target.sellingOrganizationId),
        send: () =>
          requestPriceNegotiationAsync({
            ...request,
            targetPrice: price != null ? { amount: price, currency } : null,
            sellingOrganizationIds: priceTargets.map(
              (target) => target.sellingOrganizationId
            ),
            platformProductLengthIds: priceTargets.map(
              (target) => target.platformProductLengthId
            ),
          }),
      }))
    ).then((results) => {
      const { failedSellerIds, firstReason, allRejected } =
        partitionNegotiationResults(priceGroups, results);
      if (failedSellerIds.size === 0) {
        callbacks.onSuccess();
        return;
      }
      if (allRejected) {
        callbacks.onError(firstReason);
        return;
      }
      const sent = handledTargets.filter(
        (target) => !failedSellerIds.has(target.sellingOrganizationId)
      );
      setSelectedTargets((previous) => {
        const next = new Map(previous);
        sent.forEach((target) => next.delete(target.negotiationKey));
        return next;
      });
      const failedNames = uniqueSellerOrganizations(
        handledTargets.filter((target) =>
          failedSellerIds.has(target.sellingOrganizationId)
        )
      ).map((organization) => sellerLabel(organization));
      toast.error(
        t('order_builder.price_negotiation_partial_failure', {
          sellers: failedNames.join(', '),
        })
      );
      setIsSubmittingNegotiation(false);
    });
  };

  const requestQuoteLengths: RequestQuoteLength[] = useMemo(() => {
    const lengthIds = new Set(
      targetsOfKind(requestQuoteTargets ?? negotiationTargets, 'length').map(
        (target) => target.platformProductLengthId
      )
    );
    return (platformProductLengths ?? [])
      .filter((ppl) => lengthIds.has(ppl.id))
      .map((ppl) => ({
        platformProductLengthId: ppl.id,
        label: t(
          ppl.isNominal
            ? 'order_builder.filters.length_nominal'
            : 'order_builder.filters.length_actual',
          { length: ppl.lengthDisplayValue }
        ),
        defaultQuantity: defaultRequestQuantity(
          Math.max(0, targetQuantity),
          preferredUnit,
          getPlatformProductDimensions(
            platformProduct,
            ppl.length,
            ppl.isNominal
          ),
          platformProduct.piecesPerBundle.value
        ),
        platformProductsLength: {
          isNominal: ppl.isNominal,
          platformProduct,
          length: ppl.length,
        },
      }));
  }, [
    requestQuoteTargets,
    negotiationTargets,
    platformProductLengths,
    targetQuantity,
    preferredUnit,
    platformProduct,
    t,
  ]);
  const requestQuoteOrganizations: SellerContactOrganization[] = useMemo(
    () => uniqueSellerOrganizations(requestQuoteTargets ?? []),
    [requestQuoteTargets]
  );

  const negotiateReason = negotiateDisabledReason(
    negotiationTargets,
    pendingModeNegotiationKeys
  );
  const requestQuoteReason = requestQuoteDisabledReason({
    isCannedTally,
    hasShipTo: Boolean(selectedLocationId),
    selectedCount: requestQuoteLengths.length,
  });

  const handleRequestQuoteSubmitted = (requestForQuoteId: string) => {
    toastSuccessWithLink({
      message: t('order_builder.request_quote.success'),
      linkLabel: t('order_builder.request_quote.view_rfq'),
      path: `/rfq/view/${requestForQuoteId}`,
      navigate,
    });
    setRequestQuoteTargets(null);
    setSelectedTargets(new Map());
  };

  const tabsId = useId();
  const selectedLengthsId = useId();

  const modeTabs = [
    {
      value: 'lengths' as const,
      label: t('order_builder.filters.mode_lengths'),
      disabled: false,
    },
    {
      value: 'cannedTally' as const,
      label: t('order_builder.filters.canned_tally'),
      disabled: !transportVehicleId,
    },
  ];

  const focusTab = (value: LengthMode) => {
    onModeChange(value);
    document.getElementById(`${tabsId}-tab-${value}`)?.focus();
  };

  // Arrow/Home/End move between tabs and activate as they go, per the ARIA tabs pattern.
  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const selectable = modeTabs.filter((tab) => !tab.disabled);
    if (selectable.length === 0) return;

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      focusTab(
        (event.key === 'Home' ? selectable[0] : selectable.at(-1))!.value
      );
      return;
    }

    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0 || selectable.length < 2) return;
    const current = selectable.findIndex((tab) => tab.value === mode);
    // The active tab is itself disabled, so it has no neighbour in `selectable` to step from;
    // wrapping a -1 index would send both arrows to the first tab.
    if (current === -1) return;
    event.preventDefault();
    focusTab(
      selectable[(current + step + selectable.length) % selectable.length].value
    );
  };

  return (
    <div className="bg-neut-light rounded-lg border border-neut-border shadow-neut-100 ">
      <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <h2 className="text-lg font-semibold text-neut-prim">
          {t('order_builder.comparison_title')}
        </h2>
        <div
          role="tablist"
          aria-label={t('order_builder.filters.mode')}
          data-testid="length-mode"
          className="flex items-center gap-2 overflow-x-auto"
          onKeyDown={handleTabKeyDown}
        >
          {modeTabs.map(({ value, label, disabled }) => (
            <button
              key={value}
              id={`${tabsId}-tab-${value}`}
              type="button"
              role="tab"
              aria-selected={mode === value}
              aria-controls={`${tabsId}-panel`}
              tabIndex={mode === value ? 0 : -1}
              aria-disabled={disabled}
              title={
                disabled
                  ? t('order_builder.filters.canned_tally_needs_vehicle')
                  : undefined
              }
              onClick={() => {
                if (disabled) return;
                onModeChange(value);
              }}
              className={tabTriggerClass(mode === value, disabled)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div
        role="tabpanel"
        id={`${tabsId}-panel`}
        aria-labelledby={`${tabsId}-tab-${mode}`}
      >
        <div className="flex flex-wrap items-center gap-3 px-6 py-2 border-b border-neut-border">
          {mode === 'lengths' && (
            <LengthPicker
              options={lengthFilterOptions}
              selectedIds={selectedLengthIds}
              onChange={onLengthsChange}
              selectedChipsId={selectedLengthsId}
            />
          )}
          <div className="ml-auto flex items-center gap-3">
            <Button
              onClick={() => setShowAddSellerModal(true)}
              size="sm"
              variant="secondary"
              width="10rem"
            >
              <UserPlus size={16} /> {t('order_builder.add_seller.button')}
            </Button>
            <Button
              onClick={() => {
                if (!selectedLocationId) {
                  toast.error(
                    t(
                      'order_builder.create_random_tally.shipping_address_required'
                    )
                  );
                  return;
                }
                setShowCreateRandomTallyModal(true);
              }}
              size="sm"
              variant="secondary"
              width="12rem"
            >
              <ListPlus size={16} />{' '}
              {t('order_builder.create_random_tally.button')}
            </Button>
            <ToolbarActionButton
              onClick={() => setModalTargets(negotiationTargets)}
              width="12rem"
              disabledReason={
                negotiateReason &&
                t(`order_builder.negotiate_disabled_${negotiateReason}`)
              }
            >
              <MessageSquare className="text-prim" size={16} />{' '}
              {t('order_builder.negotiate_price_button')}
            </ToolbarActionButton>
            <ToolbarActionButton
              onClick={() => setRequestQuoteTargets(negotiationTargets)}
              width="10rem"
              disabledReason={
                requestQuoteReason &&
                t(`order_builder.request_quote.disabled_${requestQuoteReason}`)
              }
            >
              <FileText className="text-prim" size={16} />{' '}
              {t('order_builder.request_quote_button')}
            </ToolbarActionButton>
          </div>
        </div>

        {mode === 'lengths' && (
          <div className="flex min-h-6 flex-wrap items-center gap-2 px-6 py-3 border-b border-neut-border">
            <SelectedLengthChips
              options={lengthFilterOptions}
              selectedIds={selectedLengthIds}
              onChange={onLengthsChange}
              id={selectedLengthsId}
            />
            <IncludeInventoryLengthsToggle
              checked={includeInventoryLengths}
              onChange={onIncludeInventoryLengthsChange}
            />
          </div>
        )}
        <div className="w-full flex items-center gap-3 p-4">
          <ComparisonToolbar
            fieldOptions={comparisonFilterFields}
            views={comparisonViews}
          />
        </div>
        {hasActiveComparisonFilter && isEmptyForActiveComparisonFilter ? (
          <div className="px-6 py-12 text-center">
            <p className="text-neut-prim font-medium">
              {t('order_builder.filter.no_matches_title')}
            </p>
            <p className="text-neut-sec text-sm mt-1">
              {t('order_builder.filter.no_matches_message')}
            </p>
            <Button
              onClick={comparisonViews.clearActiveView}
              size="sm"
              variant="secondary"
              width="auto"
              className="mt-4"
            >
              {t('order_builder.filter.clear_all')}
            </Button>
          </div>
        ) : (
          <SellerComparisonTable
            targetPrice={targetPrice}
            cartIdentifiers={cartIdentifiers}
            selectedLocationId={selectedLocationId}
            onAddToCart={handleAddToCart}
            onAddTallyToCart={handleAddTallyToCart}
            onTallyPriceEdit={handleTallyPriceEdit}
            editedTallyPriceByKey={editedTallyPriceByKey}
            onSuggestTallyPrice={(target) => setModalTargets([target])}
            onPriceEdit={handleVendorGroupPriceEdit}
            editedVendorGroupPriceByKey={editedVendorGroupPriceByKey}
            pendingTallyNegotiationKeys={pendingTallyNegotiationKeys}
            pendingNegotiationKeys={pendingNegotiationKeys}
            negotiationSelection={negotiationSelection}
            acceptedTallyPriceByKey={acceptedTallyPriceByKey}
            isCannedTally={isCannedTally}
            includeInventoryLengths={includeInventoryLengths}
            platformProduct={platformProduct}
            priceUnit={preferredUnit}
            platformProductId={platformProduct.id}
            platformProductLengthIds={effectiveLengthIds}
            transportVehicleId={transportVehicleId}
            selectedTallyName={selectedTallyName}
            vendorGroups={vendorGroups}
            isLoadingMoreVendorGroups={isLoadingMoreVendorGroups}
            hasMoreVendorGroups={hasMoreVendorGroups}
            vendorGroupsSentinelRef={vendorGroupsSentinelRef}
            cannedTallyGroups={cannedTallyGroups}
            isLoadingMoreCannedTallyGroups={isLoadingMoreCannedTallyGroups}
            hasMoreCannedTallyGroups={hasMoreCannedTallyGroups}
            cannedTallyGroupsSentinelRef={cannedTallyGroupsSentinelRef}
            sort={sort}
            onSort={onSort}
            onSellerRemoved={handleSellerRemoved}
          />
        )}
        {isVendorGroupsLoading && !vendorGroups?.length && (
          <PageLoadingSpinner name="sellers" />
        )}
      </div>

      {modalTargets && negotiationCurrency && (
        <NegotiatePriceModal
          isOpen
          onClose={() => setModalTargets(null)}
          onSubmit={handleSubmitNegotiation}
          organizations={negotiationOrganizations}
          unit={
            isCannedTally ? platformProduct.standardUnitsCode : preferredUnit
          }
          currency={negotiationCurrency}
          isCannedTally={isCannedTally}
          isSubmitting={isSubmittingNegotiation}
        />
      )}
      {requestQuoteTargets && selectedLocationId && buyerCurrency && (
        <RequestQuoteModal
          onClose={() => setRequestQuoteTargets(null)}
          onSubmitted={handleRequestQuoteSubmitted}
          productName={platformProduct.name}
          priceUnit={platformProduct.standardUnitsCode}
          lengths={requestQuoteLengths}
          organizations={requestQuoteOrganizations}
          shippingLocationId={selectedLocationId}
          defaultCurrency={buyerCurrency}
        />
      )}
      {showAddSellerModal && (
        <AddSellerModal
          isOpen={showAddSellerModal}
          onClose={() => setShowAddSellerModal(false)}
          platformProductId={platformProduct.id}
          platformProductLengthIds={effectiveLengthIds}
          excludeOrganizationIds={excludeOrganizationIds}
          onSellerAdded={onSellerAdded}
        />
      )}

      {showCreateRandomTallyModal && (
        <CreateRandomTallyModal
          isOpen={showCreateRandomTallyModal}
          onClose={() => setShowCreateRandomTallyModal(false)}
          platformProductId={platformProduct.id}
          productName={platformProduct.name}
          standardUnitsCode={platformProduct.standardUnitsCode}
          shippingLocationId={selectedLocationId}
          excludeOrganizationIds={[]}
          piecesPerBundle={platformProduct.piecesPerBundle}
        />
      )}
    </div>
  );
}
